import React, { FC, useContext, useEffect, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import styled, { css } from 'styled-components';
import { ABOUT_ME_VALUES } from '../../const';
import { UserContext } from '../../context/UserContext';
import { useDataLayerPush } from '../../hooks/useDataLayerPush';
import { useRecaptcha } from '../../hooks/useRecaptcha';
import { useMykfh_Resend_Account_ActivationMutation, useMy_Kfh_RegisterMutation } from '../../types/graphqlHelpers';
import { Kfh_PropertyTypeInterface } from '../../types/graphqlTypes';
import { isBrowser } from '../../utils/isBrowser';
import { Alert } from '../Alert';
import Blade, { BladeBody, BladeContainer, BladeFooter } from '../Blade';
import { Divider } from '../Divider';
import { Checkbox } from '../forms/Checkbox';
import { ErrorMessage } from '../forms/ErrorMessage';
import { Input } from '../forms/Input';
import { RecaptchaAction } from '../forms/RecaptchaAction';
import { SmallPrint } from '../forms/SmallPrint';
import { Panel } from '../Panel';
import { Spacer } from '../Spacer';
import { Typography } from '../Typography';
import { alertBadEmail, validateEmail } from '../../utils/emailBlackList';

const StyledResendButton = styled.button`
  ${({ theme: { colors } }) => css`
    background: none;
    text-decoration: underline;

    &:hover {
      color: ${colors.brand.default};
    }
  `}
`;

export const RegisterBlade: FC = () => {
  const [myKfhRegisterMutation, { data, loading, error }] = useMy_Kfh_RegisterMutation();
  const [myKfhResendAccountActivationMutation, { data: resendData, loading: resendLoading, error: resendError }] =
    useMykfh_Resend_Account_ActivationMutation();
  const [isSent, setIsSent] = useState(false);
  const [resendIsSent, setResendIsSent] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [aboutMeInvalid, setAboutMeInvalid] = useState(false);
  const [aboutMeStatus, setAboutMeStatus] = useState([]);
  const { dataLayerPush } = useDataLayerPush();
  const { registerData } = useContext(UserContext);

  const formMethods = useForm({
    mode: 'onSubmit',
  });

  const { register, handleSubmit, control, watch, formState } = formMethods;
  const { errors, dirtyFields, touchedFields, isSubmitted } = formState;

  const { handleReCaptchaVerify, isHuman } = useRecaptcha({ formMethods, action: 'registerBlade' });

  const password = useRef({});
  password.current = watch('password', '');

  useEffect(() => {
    function checkSuccess() {
      if (!loading && data?.myKfh?.myKfhRegister?.success) {
        setIsSent(true);

        dataLayerPush({
          event: 'formSubmit',
          formName: 'myKfhRegister',
          formValidation: 'success',
          formError: undefined,
          formRegistrationAudience: aboutMeStatus.toString(),
        });
      }
    }
    checkSuccess();
  }, [data, loading]);

  useEffect(() => {
    function checkSuccess() {
      if (!resendLoading && resendData?.myKfh?.myKfhResendAccountActivation?.success) {
        setResendIsSent(true);
      }
    }
    checkSuccess();
  }, [resendData, resendLoading]);

  // Function to handle collecting form data and  submitting the GQL mutation
  const onSubmit = (formData) => {
    alertBadEmail(formData.email);
    delete formData.captchaToken;
    const {
      firstName,
      lastName,
      email,
      password,
      passwordConfirm,
      firstTimeBuyer,
      buyer,
      seller,
      tenant,
      landlord,
      investor,
      developer,
      interested,
    } = formData;
    const redirectUrl = isBrowser ? `${window.location.pathname}${window.location.search}` : '';

    // Map form values to aboutMe array

    const aboutMe: string[] = [];

    if (firstTimeBuyer) {
      aboutMe.push(ABOUT_ME_VALUES.firstTimeBuyer);
    }
    if (buyer) {
      aboutMe.push(ABOUT_ME_VALUES.buyer);
    }
    if (seller) {
      aboutMe.push(ABOUT_ME_VALUES.seller);
    }
    if (tenant) {
      aboutMe.push(ABOUT_ME_VALUES.tenant);
    }
    if (landlord) {
      aboutMe.push(ABOUT_ME_VALUES.landlord);
    }
    if (investor) {
      aboutMe.push(ABOUT_ME_VALUES.investor);
    }
    if (developer) {
      aboutMe.push(ABOUT_ME_VALUES.developer);
    }
    if (interested) {
      aboutMe.push(ABOUT_ME_VALUES.interested);
    }

    // validate aboutMe
    if (aboutMe.length === 0) {
      setAboutMeInvalid(true);
    } else {
      setAboutMeInvalid(false);
      setAboutMeStatus(aboutMe);
    }

    // Set the user email for use in the resend event

    setUserEmail(email as string);

    // Run registration mutation

    if (aboutMe.length !== 0) {
      const myKfhRegister: Record<string, any> = {
        firstName,
        lastName,
        email,
        password,
        passwordConfirm,
        profile: {
          aboutMe,
          redirectUrl,
        },
      };

      // We can only have either a saved search or a property
      if (registerData?.action === 'saveSearch') {
        myKfhRegister.savedSearch = registerData.data;
      } else if (registerData?.action === 'saveProperty') {
        myKfhRegister.savedProperty = registerData.data;
      }

      myKfhRegisterMutation({
        variables: {
          myKfhRegister,
        },
      });
    }
  };

  const handleResendActivationEmail = () => {
    myKfhResendAccountActivationMutation({
      // variables: {
      //   myKfhResendAccountActivation: {
      //     email: userEmail,
      //   },
      // },
    });
  };

  return (
    <Blade id="register" customBlade>
      <FormProvider {...formMethods}>
        {!isSent && (
          <BladeContainer as="form" onSubmit={handleSubmit(onSubmit)} method="post" noValidate>
            <BladeBody>
              <Spacer>
                <Typography variant="displaySmall" component="h2">
                  Register for MyKFH
                </Typography>
                <Panel as="fieldset" border>
                  <Spacer spacing="half">
                    <Typography component="legend" variant="titleMedium">
                      Your details
                    </Typography>
                    <Typography variant="smallprint">*Please complete all required form fields</Typography>
                    <Input
                      type="text"
                      invalid={!!errors.firstName}
                      valid={touchedFields.firstName && dirtyFields.firstName && !errors.firstName}
                      label="First name"
                      id="firstName"
                      {...register('firstName', { required: true })}
                      required
                    />
                    <Input
                      type="text"
                      invalid={!!errors.lastName}
                      valid={touchedFields.lastName && dirtyFields.lastName && !errors.lastName}
                      label="Last name"
                      id="lastName"
                      {...register('lastName', { required: true })}
                      required
                    />
                    <Input
                      type="text"
                      invalid={!!errors.email}
                      valid={touchedFields.email && dirtyFields.email && !errors.email}
                      label="Email"
                      id="email"
                      {...register('email', { 
                        required: true,
                         validate: validateEmail, // email spam check
                         })}
                      required
                    />
                    <Input
                      type="password"
                      invalid={!!errors.password}
                      valid={touchedFields.password && dirtyFields.password && !errors.password}
                      label="Create your password"
                      id="password"
                      {...register('password', {
                        required: 'You must specify a password',
                        minLength: {
                          value: 8,
                          message: 'Password must have at least 8 characters',
                        },
                      })}
                      helpText="Must be at least 8 characters"
                      errorMessage={errors.password?.message}
                      required
                    />
                    <Input
                      type="password"
                      invalid={!!errors.passwordConfirm}
                      valid={touchedFields.passwordConfirm && dirtyFields.passwordConfirm && !errors.passwordConfirm}
                      label="Re-enter your password"
                      id="passwordConfirm"
                      {...register('passwordConfirm', {
                        required: {
                          value: true,
                          message: 'Please confirm new password',
                        },

                        validate: (value) => value === password.current || 'The passwords do not match',
                      })}
                      errorMessage={errors.passwordConfirm?.message}
                      required
                    />
                  </Spacer>
                </Panel>
                <Panel as="fieldset" border>
                  <Spacer>
                    <Spacer spacing="quarter">
                      <Typography component="legend" variant="titleMedium">
                        About you
                      </Typography>
                      <p>Which option/s best describe you?</p>
                    </Spacer>
                    <Spacer as="ul" spacing="quarter">
                      <li>
                        <Checkbox label="First-time Buyer" id="firstTimeBuyer" {...register('firstTimeBuyer', {})} />
                      </li>
                      <li>
                        <Checkbox label="Buyer" id="buyer" {...register('buyer', {})} />
                      </li>
                      <li>
                        <Checkbox label="Seller" id="seller" {...register('seller', {})} />
                      </li>
                      <li>
                        <Checkbox label="Tenant" id="tenant" {...register('tenant', {})} />
                      </li>
                      <li>
                        <Checkbox label="Landlord" id="landlord" {...register('landlord', {})} />
                      </li>
                      <li>
                        <Checkbox label="Looking to invest" id="investor" {...register('investor', {})} />
                      </li>
                      <li>
                        <Checkbox label="Developer" id="developer" {...register('developer', {})} />
                      </li>
                      <li>
                        <Checkbox label="Interested" id="interested" {...register('interested', {})} />
                      </li>
                    </Spacer>
                    {aboutMeInvalid && <ErrorMessage>Please select at least one option</ErrorMessage>}
                  </Spacer>
                </Panel>
                {data?.myKfh?.myKfhRegister?.errorDescription && (
                  <ErrorMessage>{data.myKfh.myKfhRegister.errorDescription}</ErrorMessage>
                )}
                <RecaptchaAction show={!isHuman} handleReCaptchaVerify={handleReCaptchaVerify} />
                <SmallPrint />
              </Spacer>
            </BladeBody>
            <BladeFooter
              primaryButton={{
                label: 'Next',
                onClick: handleSubmit(onSubmit),
                disableClickTracking: true,
                arrowRight: true,
              }}
            />
          </BladeContainer>
        )}

        {isSent && (
          <BladeContainer>
            <BladeBody>
              <Spacer>
                <Typography variant="displaySmall" component="h2">
                  We’ve sent you an email to check that it’s you.
                </Typography>
                <Typography variant="lead">
                  Click the link in the email to activate your account and start using MyKFH.
                </Typography>
                <Divider />
                {resendIsSent ? (
                  <Alert text="New activation email sent" />
                ) : (
                  <>
                    <div>
                      <Typography variant="titleSmall" component="h3">
                        Not received your email?
                      </Typography>
                      <StyledResendButton type="button" onClick={handleResendActivationEmail}>
                        Resend email
                      </StyledResendButton>
                    </div>
                    {resendData?.myKfh?.myKfhResendAccountActivation?.errorDescription && (
                      <ErrorMessage>{resendData.myKfh.myKfhResendAccountActivation.errorDescription}</ErrorMessage>
                    )}
                  </>
                )}
              </Spacer>
            </BladeBody>
            <BladeFooter />
          </BladeContainer>
        )}
      </FormProvider>
    </Blade>
  );
};
